import type { ApiResult, Asset, AssetBalanceHistory, AssetShare, BankSavingContract, Category, CurrencyRates, CustomCurrency, GoldMerchant, GoldPnl, GoldPnlBreakdown, GoldPrice, GoldType, NetWorthPoint, RangeSummary, SilverMerchant, SilverPnlBreakdown, SilverPrice, SilverType, Transaction, TransactionType, User, UserRole } from '@asset-mgmt/shared';

const BASE = '/api';

export interface SessionInfo {
  sessionId: string;
  createdAt: number;
  expiresAt: number;
  userAgent?: string;
  isCurrent: boolean;
}

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  const json = (await res.json()) as ApiResult<T>;

  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : undefined;
    throw new ApiError(err?.code ?? 'HTTP_ERROR', err?.message ?? `HTTP ${res.status}`);
  }

  return json.data;
}

export const api = {
  get:    <T>(path: string)                   => request<T>(path),
  post:   <T>(path: string, body: unknown)    => request<T>(path, { method: 'POST',   body: JSON.stringify(body) }),
  put:    <T>(path: string, body: unknown)    => request<T>(path, { method: 'PUT',    body: JSON.stringify(body) }),
  patch:  <T>(path: string, body: unknown)    => request<T>(path, { method: 'PATCH',  body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown)   => request<T>(path, { method: 'DELETE', ...(body ? { body: JSON.stringify(body) } : {}) }),
};

// ── Typed API helpers ──────────────────────────────────────────────────────────

export const authApi = {
  me:            ()                              => api.get<{ user: User; googleLinked: boolean; hasPassword: boolean }>('/auth/me'),
  register:      (b: { email: string; password: string; displayName: string }) =>
                   api.post<{ user: User }>('/auth/register', b),
  login:         (b: { email: string; password: string }) =>
                   api.post<{ user: User }>('/auth/login', b),
  logout:        ()                              => api.post<{ message: string }>('/auth/logout', {}),
  updateSettings:(b: Partial<User['settings']>) => api.put<{ user: User }>('/auth/settings', b),
  updateProfile: (b: { displayName: string })   => api.put<{ user: User }>('/auth/profile', b),
  telegramLink:  ()                              => api.get<{ url: string; token: string; expiresInSeconds: number }>('/auth/telegram/link'),
  changePassword:(b: { currentPassword: string; newPassword: string }) =>
                   api.put<{ message: string }>('/auth/password', b),
  getSessions:   () => api.get<SessionInfo[]>('/auth/sessions'),
  revokeSession: (sessionId: string) => api.delete<{ revoked: string }>(`/auth/sessions/${sessionId}`),
  deleteAccount: (b: { password?: string }) => api.delete<void>('/auth/account', b),
  unlinkGoogle:  () => api.delete<{ unlinked: string }>('/auth/unlink/google'),
  unlinkTelegram:() => api.delete<{ unlinked: string }>('/auth/unlink/telegram'),
};

export const currencyApi = {
  listCustom:    () =>
    api.get<CustomCurrency[]>('/currencies/custom'),
  listStandard:  () =>
    api.get<string[]>('/currencies'),
  getRates:      (base: string) =>
    api.get<CurrencyRates>(`/currencies/rates?base=${encodeURIComponent(base)}`),
  createCustom:  (b: Pick<CustomCurrency, 'code' | 'name' | 'baseCode' | 'multiplier'> & { symbol?: string }) =>
    api.post<CustomCurrency>('/currencies/custom', b),
  deleteCustom:  (id: string) =>
    api.delete<{ message: string }>(`/currencies/custom/${id}`),
};

export type CategoryPayload = {
  name: string;
  color?: string;
  icon?: string;
  type?: TransactionType | 'any';
};

export const categoryApi = {
  list:   () => api.get<Category[]>('/categories'),
  create: (b: CategoryPayload) => api.post<Category>('/categories', b),
  update: (id: string, b: Partial<CategoryPayload>) => api.put<Category>(`/categories/${id}`, b),
  remove: (id: string) => api.delete<{ message: string }>(`/categories/${id}`),
};

export type TransactionPayload = {
  assetId: string;
  toAssetId?: string;
  categoryId: string;
  type: TransactionType;
  amount: number;
  currency: string;
  note?: string;
  description?: string;
  date: string;
  // Gold-specific fields
  goldQuantity?: number;
  unitPrice?: number;
  merchantId?: string;
  goldType?: GoldType;
  /** Cash/bank asset to debit (buy) or credit (sell) alongside this gold transaction */
  fundAssetId?: string;
  // Silver-specific fields
  silverQuantity?: number;
  silverType?: SilverType;
};

export const transactionApi = {
  list:      (year: string, month: string) =>
    api.get<Transaction[]>(`/transactions?year=${year}&month=${month}`),
  listRange: (from: string, to: string) =>
    api.get<Transaction[]>(`/transactions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  create: (b: TransactionPayload) =>
    api.post<Transaction>('/transactions', b),
  update: (id: string, b: Partial<TransactionPayload> & { originalDate?: string }) =>
    api.put<Transaction>(`/transactions/${id}`, b),
  remove: (id: string) =>
    api.delete<{ deleted: string }>(`/transactions/${id}`),
  searchNotes: (q: string) =>
    api.get<string[]>(`/transactions/search-notes?q=${encodeURIComponent(q)}`),
};

export const assetApi = {
  list: () => api.get<Asset[]>('/assets'),
  listTransactions: (assetId: string, from = '2020-01-01') => {
    const to = new Date(new Date().getFullYear() + 1, 11, 31).toISOString().slice(0, 10);
    return api.get<Transaction[]>(
      `/transactions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&assetId=${encodeURIComponent(assetId)}`,
    );
  },
  getShares:    (assetId: string) => api.get<AssetShare[]>(`/assets/${assetId}/shares`),
  addShare:     (assetId: string, b: { email: string; permission: 'editor' | 'viewer' }) =>
                  api.post<AssetShare>(`/assets/${assetId}/shares`, b),
  updateShare:  (assetId: string, shareId: string, b: { permission: 'editor' | 'viewer' }) =>
                  api.put<AssetShare>(`/assets/${assetId}/shares/${shareId}`, b),
  removeShare:  (assetId: string, shareId: string) =>
                  api.delete<{ deleted: string }>(`/assets/${assetId}/shares/${shareId}`),
  getSharedWithMe: () => api.get<Array<{ share: AssetShare; asset: Asset; ownerName: string }>>('/assets/shares/with-me'),
};

export interface AdminUser extends Omit<User, 'googleSub' | 'telegramChatId'> {}

export const adminApi = {
  listUsers:   () => api.get<AdminUser[]>('/admin/users'),
  getUser:     (userId: string) => api.get<AdminUser>(`/admin/users/${userId}`),
  updateUser:  (userId: string, b: { displayName?: string; email?: string; role?: UserRole }) =>
                 api.put<AdminUser>(`/admin/users/${userId}`, b),
  setRole:     (userId: string, role: UserRole) =>
                 api.put<{ userId: string; role: UserRole }>(`/admin/users/${userId}/role`, { role }),
  deleteUser:  (userId: string) => api.delete<void>(`/admin/users/${userId}`),
  forceLogout: (userId: string) => api.delete<{ revoked: number }>(`/admin/users/${userId}/sessions`),
};

export const summaryApi = {
  netWorth:      (year?: string) => api.get<NetWorthPoint[]>(`/summary/net-worth${year ? `?year=${year}` : ''}`),
  monthly:       (year?: string) => api.get<import('@asset-mgmt/shared').MonthlySummary[]>(`/summary/monthly${year ? `?year=${year}` : ''}`),
  byCategory:    (params?: { year?: string; month?: string; type?: 'income' | 'expense' }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return api.get<import('@asset-mgmt/shared').CategoryBreakdown[]>(`/summary/by-category${q ? `?${q}` : ''}`);
  },
  assetHistory:  (year?: string) => api.get<AssetBalanceHistory[]>(`/summary/asset-history${year ? `?year=${year}` : ''}`),
  range:         (from: string, to: string) =>
    api.get<RangeSummary>(`/summary/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
};
// ── Gold API helpers ──────────────────────────────────────────────────────────────────────────────────
export const goldApi = {
  getMerchants: () =>
    api.get<GoldMerchant[]>('/gold/merchants'),
  getPrice: (merchantId: string, goldType: GoldType = 'ring') =>
    api.get<GoldPrice>(`/gold/prices/${encodeURIComponent(merchantId)}?goldType=${encodeURIComponent(goldType)}`),
  getPnl: (assetId: string) =>
    api.get<GoldPnlBreakdown>(`/gold/pnl/${encodeURIComponent(assetId)}`),
  getPortfolioPnl: () =>
    api.get<GoldPnlBreakdown | null>('/gold/portfolio-pnl'),
};// ── Silver API helpers ──────────────────────────────────────────────────────────────────────────────────────────────
export const silverApi = {
  getMerchants: () =>
    api.get<SilverMerchant[]>('/silver/merchants'),
  getPrice: (merchantId: string, silverType: SilverType = 'bar') =>
    api.get<SilverPrice>(`/silver/prices/${encodeURIComponent(merchantId)}?silverType=${encodeURIComponent(silverType)}`),
  getPnl: (assetId: string) =>
    api.get<SilverPnlBreakdown>(`/silver/pnl/${encodeURIComponent(assetId)}`),
  getPortfolioPnl: () =>
    api.get<SilverPnlBreakdown | null>('/silver/portfolio-pnl'),
};// ── Google Sheets integration ──────────────────────────────────────────────────

export type SheetsStatus =
  | { connected: false }
  | {
      connected:      true;
      spreadsheetId:  string;
      spreadsheetUrl: string;
      connectedAt:    string;
      lastSyncAt:     string | null;
      lastSyncError:  string | null;
    };

export const sheetsApi = {
  /** Returns the current connection status */
  status: () => api.get<SheetsStatus>('/sheets/status'),

  /** Returns the URL to redirect the browser to for OAuth consent */
  connectUrl: () => `${BASE}/sheets/connect`,

  /** Trigger a full manual resync */
  sync: () => api.post<{ message: string }>('/sheets/sync', {}),

  /** Disconnect and remove config (spreadsheet stays on Google Drive) */
  disconnect: () => api.delete<{ disconnected: boolean }>('/sheets/disconnect'),
};

// ── Bank Saving API helpers ───────────────────────────────────────────────

export type OpenContractPayload = {
  assetId: string;
  bankName: string;
  principal: number;
  currency: string;
  interestRate: number;
  periodMonths: number;
  openDate: string;
  estimatedEndDate: string;
  estimatedEndValue: number;
  sourceAssetId?: string;
  categoryId?: string;
  note?: string;
};

export type CloseContractPayload = {
  receivedAmount: number;
  receivedDate: string;
  targetAssetId?: string;
  categoryId?: string;
  note?: string;
};

export type WithdrawContractPayload = {
  withdrawalPrincipal: number;
  receivedAmount: number;
  receivedDate: string;
  targetAssetId?: string;
  categoryId?: string;
  note?: string;
};

export type RotateContractPayload = {
  periodMonths: number;
  interestRate: number;
  bankName?: string;
  principal?: number;
  openDate?: string;
  note?: string;
};

export const bankSavingApi = {
  listContracts:   (assetId: string) =>
    api.get<BankSavingContract[]>(`/bank-saving/contracts/${encodeURIComponent(assetId)}`),
  openContract:    (payload: OpenContractPayload) =>
    api.post<BankSavingContract>('/bank-saving/contracts', payload),
  closeContract:   (contractId: string, payload: CloseContractPayload) =>
    api.post<BankSavingContract>(`/bank-saving/contracts/${encodeURIComponent(contractId)}/close`, payload),
  withdrawPartial: (contractId: string, payload: WithdrawContractPayload) =>
    api.post<BankSavingContract>(`/bank-saving/contracts/${encodeURIComponent(contractId)}/withdraw`, payload),
  rotateContract:  (contractId: string, payload: RotateContractPayload) =>
    api.post<{ closedContract: string; newContract: BankSavingContract }>(
      `/bank-saving/contracts/${encodeURIComponent(contractId)}/rotate`, payload,
    ),
};
